import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Add } from '@mui/icons-material';
import { proofDocumentTypeOptions } from './options';
import { AddDocBox } from '../AdditionalDocs/AddDocBox';
import { PAGE_TEXT } from '../lang';
import {
  type AdditionalDocFormData,
  addApplicationItem,
  useMoveApplicationItem,
  updateApplicationItem,
} from '../../../graphql';
import {
  Alert,
  type AlertDetails,
  Accordion,
  IconButton,
} from '../../../../../components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { handleFileRename } from '../../../../../utils/handleFileRename';
import { type FormController } from '../../../../../utils';

type ProofDocumentProps = {
  applicationNo: string;
  proofDocuments: AdditionalDocFormData[];
  enableEdit: FormController;
  enableDelete: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const { PROOF_DOCUMENT } = PAGE_TEXT;
export const ProofDocument: React.FC<ProofDocumentProps> = (
  props: ProofDocumentProps,
) => {
  const { applicationId } = useParams();
  const {
    applicationNo = '',
    proofDocuments,
    enableEdit,
    refetchApplication,
    enableDelete,
    handleLoading,
  } = props;

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [keyIds, setKeyIds] = useState<string[]>(
    proofDocuments.map(() => uuidv4()),
  );

  function handleAddDoc(): void {
    setKeyIds(() => [...keyIds, uuidv4()]);
  }

  const removeApplicationItem = useMoveApplicationItem(refetchApplication);
  const handleDelete =
    (index: number): (() => Promise<void>) =>
    async () => {
      if (index >= proofDocuments.length) {
        const newKeyIds = [...keyIds];
        newKeyIds.splice(index, 1);
        setKeyIds(() => newKeyIds);
        setOpenAlert(true);
        setAlertDetails({
          alertContent: 'Deleted Additional Document Successfully.',
          alertType: 'success',
        });
        return;
      }

      await handleLoading(async () => {
        try {
          const id = proofDocuments[index]._id;
          const variables = {
            id: applicationId,
            input: {
              proofDocumentId: id,
            },
          };
          await removeApplicationItem({ variables });
          const newKeyIds = [...keyIds];
          newKeyIds.splice(index, 1);
          setKeyIds(() => newKeyIds);
          setAlertDetails({
            alertContent: 'Deleted Proof Document Successfully.',
            alertType: 'success',
          });
        } catch (e) {
          setAlertDetails({
            alertContent:
              'Deleted Proof Document Failed.' + (e as Error)?.message,
            alertType: 'error',
          });
        }
      });
      setOpenAlert(true);
    };

  const createProofDocument = addApplicationItem(
    'proofDocuments',
    refetchApplication,
  );
  const updateProofDocument = updateApplicationItem();
  const handleSubmit =
    (index: number) =>
    async (values: AdditionalDocFormData): Promise<void> => {
      const { supportingDocuments, documentType } = values;
      if (!supportingDocuments.length) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: 'Please upload relevant supporting documents.',
          alertType: 'warning',
        });
        return;
      }
      await handleLoading(async () => {
        try {
          const isCreateMode = index >= proofDocuments.length;

          const inputValue = {
            documentType,
            supportingDocuments: supportingDocuments.map((doc, index) => {
              const { name, ...other } = doc;
              return {
                name: handleFileRename(
                  applicationNo,
                  getOptionsLabel(proofDocumentTypeOptions, documentType)
                    .replaceAll(' ', '_')
                    .toLocaleLowerCase(),
                  index + 1,
                ),
                ...other,
              };
            }),
          };
          if (isCreateMode) {
            await createProofDocument({
              variables: {
                id: applicationId,
                input: {
                  proofDocuments: {
                    ...inputValue,
                    applicationId,
                  },
                },
              },
            });
          } else {
            await updateProofDocument({
              variables: {
                id: applicationId,
                updateId: proofDocuments[index]._id,
                input: {
                  proofDocuments: {
                    ...inputValue,
                  },
                },
              },
            });
          }
          refetchApplication();
          alertDetailDef = {
            alertContent: 'Saved Proof Document Successfully.',
            alertType: 'success',
          };
        } catch (e) {
          alertDetailDef = {
            alertContent: 'Error Occurs.' + (e as Error)?.message,
            alertType: 'error',
          };
        } finally {
          setAlertDetails(alertDetailDef);
        }
      });
      setOpenAlert(true);
    };

  const initialProofDocumentData = {
    documentType: '',
    supportingDocuments: [],
  };

  return (
    <div>
      {keyIds.map((keyId, index) => (
        <Accordion
          key={index}
          className="w-full"
          defaultExpanded
          title={`${PROOF_DOCUMENT.TITLE} - ${index + 1}`}
          details={
            <AddDocBox
              key={keyId}
              InitialAdditionalDocData={
                index >= proofDocuments.length
                  ? Object.assign({}, initialProofDocumentData)
                  : proofDocuments[index]
              }
              boxText={{
                subtitle: PROOF_DOCUMENT.TITLE,
                caption: PROOF_DOCUMENT.CAPTION,
                filesPanel: {
                  title: PAGE_TEXT.ADDITIONAL_DOCUMENTS.FILES_PANEL.PANEL_TITLE,
                  caption:
                    PAGE_TEXT.ADDITIONAL_DOCUMENTS.FILES_PANEL.PANEL_CAPTION,
                },
              }}
              enableEdit={enableEdit}
              enableDelete={enableDelete}
              addDocIndex={index}
              docOptions={proofDocumentTypeOptions}
              handleSubmit={handleSubmit(index)}
              handleDelete={handleDelete(index)}
              handleLoading={handleLoading}
            />
          }
        />
      ))}
      {enableEdit && (
        <div
          style={{
            marginTop: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            padding: '0.5rem 1rem',
          }}
        >
          <IconButton
            icon={
              <Add
                style={{
                  backgroundColor: '#951d1d',
                  color: '#fff',
                  borderRadius: '50%',
                  cursor: 'pointer',
                }}
              />
            }
            onClick={handleAddDoc}
          />
          {PAGE_TEXT.ADDITIONAL_DOCUMENTS.ADD_MORE_DOCS}
        </div>
      )}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </div>
  );
};
